import { useState } from 'react'
import { laneLabel, type SupportLane } from '../lib/support'
import { NAME_TOKEN, type SavedReply } from '../lib/supportReplies'

/**
 * The lane's saved replies — the list and the form (122, HLP-30).
 *
 * ── IT IS IN components/, NOT IN THE ROUTE FILE ─────────────────────────────
 *
 * Importing a route pulls in AppShell and then an SVG the test bundler cannot
 * resolve, so anything defined in `routes/` is unreachable by the web suite.
 * Everything here takes its data as props for the same reason.
 *
 * ── A STAFF SCREEN, ENGLISH ON PURPOSE ──────────────────────────────────────
 *
 * Same posture as SupportStaffPanel: the lane holder's tools stay English
 * (SHL-25's second pass). The picker in the composer is in hubCopy because
 * the thread is one component for both readers.
 *
 * ── WHAT IT DOES NOT DO ─────────────────────────────────────────────────────
 *
 * No use count, no "last used", no per-holder anything (B22, 122's header).
 * No delete confirmation dialog: a removed reply is a template nobody outside
 * the lane saw, it costs one form to write again, and a dialog that fires on
 * every press is the one nobody reads. The event log keeps who removed what.
 */
export function SavedReplies({
  lanes,
  rows,
  onSave,
  onRemove,
  busy = false,
  error,
}: {
  /** The lanes this person HOLDS. The form offers exactly these; the server
   *  refuses any other (rule 21). */
  lanes: SupportLane[]
  rows: SavedReply[]
  onSave: (id: string | null, lane: SupportLane, title: string, body: string) => void
  onRemove: (id: string) => void
  busy?: boolean
  error?: string | null
}) {
  const [editing, setEditing] = useState<string | 'new' | null>(null)
  const [lane, setLane] = useState<SupportLane>(lanes[0])
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')

  function startNew() {
    setEditing('new')
    setLane(lanes[0])
    setTitle('')
    setBody('')
  }
  function startEdit(r: SavedReply) {
    setEditing(r.id)
    setLane(r.lane)
    setTitle(r.title)
    setBody(r.body)
  }
  function stop() {
    setEditing(null)
    setTitle('')
    setBody('')
  }

  const form = (
    <form
      className="supportstaff__form savedreplies__form"
      onSubmit={(e) => {
        e.preventDefault()
        if (!title.trim() || !body.trim()) return
        onSave(editing === 'new' ? null : editing, lane, title.trim(), body.trim())
        stop()
      }}
    >
      {editing === 'new' && lanes.length > 1 && (
        <>
          <label className="supportstaff__label" htmlFor="savedreply-lane">Lane</label>
          <select
            id="savedreply-lane"
            className="supportstaff__select"
            value={lane}
            onChange={(e) => setLane(e.target.value as SupportLane)}
            disabled={busy}
          >
            {lanes.map((l) => (
              <option key={l} value={l}>{laneLabel(l)}</option>
            ))}
          </select>
        </>
      )}
      {editing !== 'new' && (
        // The lane of a saved reply is not editable (122): it is the row's
        // home. Said, not offered.
        <p className="supportstaff__state">In {laneLabel(lane)}.</p>
      )}
      <label className="supportstaff__label" htmlFor="savedreply-title">Title</label>
      <input
        id="savedreply-title"
        className="composer__body supportstaff__title"
        type="text"
        maxLength={80}
        placeholder="What the picker shows — “Check Junk”"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        disabled={busy}
      />
      <label className="supportstaff__label" htmlFor="savedreply-body">Reply</label>
      <textarea
        id="savedreply-body"
        className="composer__body"
        rows={4}
        maxLength={3000}
        placeholder={`Lands in the reply box for you to edit before sending. ${NAME_TOKEN} becomes their first name.`}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        disabled={busy}
      />
      <div className="supportstaff__actions">
        <button type="submit" className="btn btn--primary btn--sm" disabled={busy || !title.trim() || !body.trim()}>
          {editing === 'new' ? 'Add it' : 'Save changes'}
        </button>
        <button type="button" className="btn btn--quiet btn--sm" disabled={busy} onClick={stop}>
          Cancel
        </button>
      </div>
    </form>
  )

  return (
    <div className="savedreplies">
      <p className="supportinbox__lede">
        Standard answers for the lanes you hold. Picking one puts it in the reply box; nothing is sent until you press Reply.
        Anybody who answers the lane can change these.
      </p>
      {error && (
        <p className="roles__error" role="alert">
          {error}
        </p>
      )}
      {rows.length === 0 && editing !== 'new' && (
        <p className="supportstaff__empty">None yet. Launch week will be the same five answers many times over — write them once.</p>
      )}
      {rows.length > 0 && (
        <ul className="savedreplies__list">
          {rows.map((r) =>
            editing === r.id ? (
              <li key={r.id} className="savedreplies__row">{form}</li>
            ) : (
              <li key={r.id} className="savedreplies__row">
                <div className="savedreplies__head">
                  <strong className="savedreplies__title">{r.title}</strong>
                  {lanes.length > 1 && <span className="pill">{laneLabel(r.lane)}</span>}
                </div>
                <p className="savedreplies__body">{r.body}</p>
                <div className="supportstaff__actions">
                  <button type="button" className="btn btn--quiet btn--sm" disabled={busy || editing !== null} onClick={() => startEdit(r)}>
                    Edit
                  </button>
                  <button type="button" className="btn btn--quiet btn--sm" disabled={busy || editing !== null} onClick={() => onRemove(r.id)}>
                    Remove
                  </button>
                </div>
              </li>
            ),
          )}
        </ul>
      )}
      {editing === 'new' ? (
        form
      ) : (
        <button type="button" className="btn btn--sm" disabled={busy || editing !== null} onClick={startNew}>
          Add a saved reply
        </button>
      )}
    </div>
  )
}
